<script lang="ts">
  /**
   * F7 · The week, drawn.
   *
   * Two shapes, one component, because they are the same week and must never
   * disagree about which day it is:
   *
   *  · `matrix` — seven day columns, four named people in each. This is the feature
   *    as specified: *who is where, school runs, sport, who fetches whom*. It is
   *    what Home shows, and what the kitchen tablet shows on a Sunday.
   *  · `ribbon` — seven cells, one row, no people. What the tablet carries on every
   *    other day, under the clock. A wall screen has room for one week or one set of
   *    today's tasks and not both, so the week is a strip until Sunday promotes it.
   *
   * **Every cell is an em dash today, and that is the honest answer**, not a
   * placeholder. `lib/week.svelte.ts` explains why there is no calendar to read and
   * what would change that. Nothing on this screen is invented: the seven dates are
   * computed, the four names are the family, and the only entries drawn are
   * deadlines the estate genuinely posted to the attention feed — which are marked
   * as deadlines and kept out of the people grid, because a licence expiring is not
   * an appointment and nobody is expected to be anywhere.
   *
   * ## Why the grid is not inside `Panel`
   *
   * `Panel` arbitrates loading, failed and empty for something that was *requested*.
   * The week grid is not requested — it is seven dates and four names, and it cannot
   * fail. What can be empty is the calendar behind it, and that is what the `Panel`
   * underneath the grid carries, in the same shape the kitchen tablet already uses
   * for its unwired figures: draw the thing, then say plainly why it is blank. A
   * `Panel` wrapped round the whole grid would hide the shape at exactly the moment
   * the shape is the only thing there is to show.
   */
  import { Icon, Panel, DASH } from "@steyncd/steyn-design";
  import { dayLabel, houseISO } from "../lib/housetime";
  import { CALENDAR, PEOPLE, week, type PersonId, type WeekEntry, type WeekMark } from "../lib/week.svelte";

  let {
    variant = "matrix",
    now = Date.now(),
    marks = [],
    /**
     * False when the attention feed could not be read — a `display` tablet gets a
     * 403 from `GET /attention`, always. Without this the deadline strip would say
     * "nothing is due this week", which is a claim about the estate rather than a
     * fact about this screen's permissions.
     */
    marksKnown = true,
    /**
     * One line per person instead of two, for the kitchen tablet's Sunday card.
     *
     * The same layout the narrow breakpoint already uses, applied by choice rather
     * than by width: that card is 812px wide and 230px tall, so it is the *height*
     * that is scarce, and no media query can express "wide but short".
     */
    compact = false,
    /**
     * The trailing `Panel` explaining the missing calendar, and the separate list of
     * what falls due this week.
     *
     * Off on the wall. Not because the wall may lie — the ribbon-style line naming
     * the missing source still renders — but because the deadlines are already drawn
     * on the day they fall in the grid above, and a screen that cannot scroll cannot
     * afford to say the same thing twice. On Home, where there is a scrollbar and a
     * reader who came to look, both are worth the room.
     */
    notes = true,
  }: {
    variant?: "matrix" | "ribbon";
    now?: number;
    marks?: WeekMark[];
    marksKnown?: boolean;
    compact?: boolean;
    notes?: boolean;
  } = $props();

  const range = $derived(week.range(now));
  const days = $derived(week.days(range.from));
  const today = $derived(houseISO(now));

  const NAME: Record<string, string> = Object.fromEntries(PEOPLE.map((p) => [p.id, p.name]));

  function marksOn(iso: string): WeekMark[] {
    return marks.filter((m) => m.date === iso);
  }

  /**
   * "Mandri fetches Liam and Eben", or just "a lift" when the calendar named a lift
   * without naming a driver.
   *
   * The driver is the part that matters and the part most likely to be missing, so
   * the sentence degrades to something true rather than to "undefined fetches".
   */
  function liftWords(e: WeekEntry): string {
    const l = e.lift;
    if (!l) return "";
    const riders = l.who.map((id: PersonId) => NAME[id] ?? id);
    const who = riders.length === 0 ? "" : riders.length === 1 ? riders[0]! : `${riders.slice(0, -1).join(", ")} and ${riders.at(-1)}`;
    if (!l.by) return who ? `a lift for ${who}` : "a lift";
    return who ? `${NAME[l.by] ?? l.by} fetches ${who}` : `${NAME[l.by] ?? l.by} drives`;
  }
</script>

{#if variant === "ribbon"}
  <!-- ── The ribbon ───────────────────────────────────────────────────────────
       Seven cells and nothing else. It sits under the tablet's clock rather than in
       the card grid, so it costs about seventy pixels and the five cards below it
       keep their places — a wall screen that has to be scrolled has lost a card, and
       a card that has scrolled off does not exist. -->
  <div class="ribbon" aria-label={range.label}>
    {#each days as iso (iso)}
      {@const d = dayLabel(iso)}
      {@const m = marksOn(iso)}
      <!-- One line per day, not a stack. The ribbon's whole justification is that it
           costs about sixty pixels on a screen where a sixth card would have pushed
           the fifth off the bottom, and a two-line cell spends that twice over. -->
      <div class="rcell" class:rcell--today={iso === today}>
        <span class="rcell__d">
          {d.name}<span class="rcell__n num">{d.num}</span>
          {#if iso === today}<span class="rcell__now">today</span>{/if}
        </span>
        {#if m.length > 0}
          <span class="rcell__due"><Icon name="clock" size={12} /> {m.length} due</span>
        {:else}
          <span class="rcell__dash">{DASH}</span>
        {/if}
      </div>
    {/each}
  </div>
  <p class="slot slot--ribbon">
    <span class="slot__l">Who is where, school runs, lifts</span>
    {DASH} {CALENDAR.why}
  </p>
{:else}
  <!-- ── The matrix ───────────────────────────────────────────────────────────
       Day columns rather than a person-by-day table with a name gutter. Two reasons,
       and the second is the one that decided it: a column per day survives becoming
       a stack on a phone with no second markup and no sideways scroll, and a cell
       that carries its own person's name reads as "Liam, nothing" instead of as a
       dash in a grid you have to count across to decode. With no calendar wired,
       twenty-eight anonymous dashes would be exactly that. -->
  <div class="matrix" class:matrix--compact={compact}>
    {#each days as iso (iso)}
      {@const d = dayLabel(iso)}
      <section class="col" class:col--today={iso === today}>
        <h3 class="col__h">
          <span class="col__day">{d.name}</span>
          <span class="col__num num">{d.num}</span>
          {#if iso === today}<span class="col__now">today</span>{/if}
        </h3>

        {#each PEOPLE as p (p.id)}
          {@const entries = week.entriesFor(p.id, iso)}
          <div class="cell">
            <span class="cell__who">{p.short}</span>
            {#if entries.length === 0}
              <span class="cell__dash">{DASH}</span>
            {:else}
              {#each entries as e (e.id)}
                <!-- Lifts in bold, and never bold alone: the car glyph and the words
                     "Mandri fetches Liam" carry it too, so a greyscale photograph of
                     the fridge screen still says who is driving. -->
                <span class="ev" class:ev--lift={e.lift !== null}>
                  {#if e.at}<span class="ev__at num">{e.at}</span>{/if}
                  <span class="ev__what">{e.what}</span>
                  {#if e.where}<span class="ev__where">{e.where}</span>{/if}
                  {#if e.lift}
                    <span class="ev__lift"><Icon name="car" size={12} /> {liftWords(e)}</span>
                  {/if}
                </span>
              {/each}
            {/if}
          </div>
        {/each}

        {#each marksOn(iso) as m (m.id)}
          <!-- A deadline, not an appointment, and marked as one. It is on this day
               because the estate said so, and it links back to whichever portal
               said it — never an answer without its receipt. -->
          <a class="due" class:due--now={m.overdue || m.urgent} href={m.deepLink}>
            <Icon name={m.overdue ? "alert" : "clock"} size={12} />
            <span>{m.overdue ? "Overdue" : "Due"} · {m.text}</span>
            <span class="due__src">from {m.source}</span>
          </a>
        {/each}
      </section>
    {/each}
  </div>

  {#if !notes}
    <!-- The wall's short form of the same statement. The gap is still named; it is
         named in one line instead of a panel. -->
    <p class="slot slot--ribbon">
      <span class="slot__l">Who is where, school runs, lifts</span>
      {DASH} {CALENDAR.why}
    </p>
  {:else}
  <!-- The calendar itself. See the note at the top for why this sits under the grid
       rather than around it. -->
  <Panel isEmpty={week.state === "unwired"}>
    {#snippet empty()}
      {CALENDAR.why}
      <span class="next">{CALENDAR.next}</span>
    {/snippet}
    {#snippet children()}{/snippet}
  </Panel>

  <!-- What the estate does know about this week. Real, and separately stated so it
       is never mistaken for the calendar filling in. -->
  <div class="dl">
    <h3 class="dl__h">Expiring and booked, this week</h3>
    <!-- `isEmpty` only when the feed was actually read. Unreadable is a different
         state and it renders as a sentence below, never as an empty list. -->
    <Panel isEmpty={marksKnown && marks.length === 0}>
      {#snippet empty()}
        Nothing in the estate falls due this week. Vault posts expiring documents here and
        Waypoint posts bookings.
        <span class="next">Nothing to do. This is the good state.</span>
      {/snippet}
      {#if !marksKnown}
        <p class="dl__denied">
          <Icon name="alert" size={13} />
          <span
            ><strong>This screen may not read the estate's deadlines.</strong> That is not the same
            as nothing being due.
            <span class="next">Read them on the Front Door, signed in.</span></span
          >
        </p>
      {:else}
        <ul class="dl__list">
          {#each marks as m (m.id)}
            {@const d = dayLabel(m.date)}
            <li>
              <a class="dl__row" href={m.deepLink}>
                <span class="dl__day num">{d.name} {d.num}</span>
                <span class="dl__txt">{m.text}</span>
                <span class="dl__src">
                  {#if m.overdue}<span class="dl__flag"><Icon name="alert" size={12} /> overdue</span>{/if}
                  from {m.source}
                </span>
              </a>
            </li>
          {/each}
        </ul>
      {/if}
    </Panel>
  </div>
  {/if}
{/if}

<style>
  /* ── The ribbon ─────────────────────────────────────────────────────────── */
  .ribbon {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 6px;
  }
  .rcell {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    padding: 6px 9px;
    border-radius: var(--r-control, 10px);
    background: var(--fill);
    box-shadow: inset 0 0 0 1px var(--line);
    min-width: 0;
  }
  /* Today, in copper — and never copper alone: the word "today" sits under the
     date, so the marker survives a greyscale screenshot and a colour-blind reader. */
  .rcell--today {
    box-shadow: inset 0 0 0 1px var(--acc);
  }
  .rcell__d {
    display: flex;
    align-items: baseline;
    gap: 5px;
    font-size: var(--fs-micro);
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--tx2);
  }
  .rcell__n {
    font-weight: 700;
    color: var(--mut);
  }
  .rcell__now {
    font-size: var(--fs-micro);
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--acc);
  }
  .rcell__dash,
  .rcell__due {
    font-size: var(--fs-micro);
    color: var(--mut);
  }
  .rcell__due {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-weight: 800;
    color: var(--warn);
  }

  /* ── The matrix ─────────────────────────────────────────────────────────── */
  .matrix {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 8px;
  }
  .col {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 10px;
    border-radius: var(--r-control, 10px);
    background: var(--s1);
    box-shadow: inset 0 0 0 1px var(--line);
    min-width: 0;
  }
  .col--today {
    box-shadow: inset 0 0 0 1px var(--acc);
  }
  .col__h {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 5px;
    margin: 0 0 2px;
    padding-bottom: 6px;
    border-bottom: 1px solid var(--line);
    font-size: var(--fs-micro);
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--tx2);
  }
  .col__num {
    font-weight: 700;
    color: var(--mut);
  }
  .col__now {
    font-weight: 800;
    color: var(--acc);
  }

  .cell {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  .cell__who {
    font-size: var(--fs-micro);
    font-weight: 700;
    color: var(--mut);
  }
  /* The em dash for a person with nothing on a day. Muted, because it is the absence
     of an entry and must not read like one. */
  .cell__dash {
    font-size: var(--fs-small);
    color: var(--mut);
  }

  .ev {
    display: flex;
    flex-direction: column;
    gap: 1px;
    font-size: var(--fs-small);
    line-height: 1.35;
    color: var(--tx);
  }
  .ev--lift {
    font-weight: 800;
  }
  .ev__at {
    font-size: var(--fs-micro);
    font-weight: 700;
    color: var(--tx2);
  }
  .ev__where {
    font-size: var(--fs-micro);
    color: var(--mut);
  }
  .ev__lift {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: var(--fs-micro);
    font-weight: 800;
    color: var(--tx2);
  }

  .due {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 5px;
    margin-top: 2px;
    padding: 5px 7px;
    border-radius: var(--r-control, 10px);
    border-left: 3px solid var(--ok);
    background: var(--fill);
    font-size: var(--fs-micro);
    font-weight: 700;
    color: var(--tx2);
  }
  .due--now {
    border-left-color: var(--warn);
    color: var(--warn);
  }
  .due__src {
    font-weight: 600;
    color: var(--mut);
  }

  /* ── The named gap, and the next action ─────────────────────────────────── */
  .slot {
    margin: 8px 0 0;
    font-size: var(--fs-micro);
    line-height: 1.45;
    color: var(--mut);
  }
  .slot__l {
    font-weight: 800;
    color: var(--tx2);
  }
  .slot--ribbon {
    margin-top: 6px;
  }
  .next {
    display: block;
    margin-top: 5px;
    font-weight: 700;
    color: var(--tx2);
  }

  /* ── Deadlines ──────────────────────────────────────────────────────────── */
  .dl {
    margin-top: 12px;
  }
  .dl__h {
    margin: 0 0 8px;
    font-size: var(--fs-micro);
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--mut);
  }
  .dl__list {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .dl__row {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 10px;
    padding: 9px 11px;
    border-radius: var(--r-control, 10px);
    background: var(--s1);
    box-shadow: inset 0 0 0 1px var(--line);
    font-size: var(--fs-small);
    color: var(--tx);
  }
  .dl__row:hover {
    box-shadow: inset 0 0 0 1px var(--acc);
  }
  .dl__day {
    font-size: var(--fs-micro);
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--tx2);
    flex-shrink: 0;
  }
  .dl__txt {
    flex: 1 1 220px;
    min-width: 0;
  }
  .dl__src {
    font-size: var(--fs-micro);
    color: var(--mut);
  }
  .dl__flag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-weight: 800;
    color: var(--warn);
  }
  .dl__denied {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    margin: 0;
    font-size: var(--fs-small);
    line-height: 1.5;
    color: var(--tx2);
  }
  .dl__denied :global(svg) {
    flex-shrink: 0;
    margin-top: 2px;
    color: var(--warn);
  }

  /* One line per person. Identical to what the narrow breakpoint below does to the
     cells, applied by prop for a card that is wide and short. */
  .matrix--compact .cell {
    flex-direction: row;
    align-items: baseline;
    gap: 7px;
  }
  .matrix--compact .col {
    gap: 2px;
    padding: 6px 8px;
  }
  /* A wall tablet in a mount is more often 1024×768 than 1280×800, and at that size
     seven day columns and a card of anything else have to share one row. Four pixels
     a column is twenty-eight across the week, which is the difference between the
     grid fitting its card and overflowing it. */
  @media (max-height: 820px) {
    .matrix--compact .col {
      padding: 4px 6px;
    }
  }
  .matrix--compact .col__h {
    margin-bottom: 0;
    padding-bottom: 4px;
  }
  .matrix--compact + .slot {
    margin-top: 5px;
  }

  /* ── Narrow ───────────────────────────────────────────────────────────────
     Seven columns become seven stacked days. Never a sideways scroll, which is what
     a week table does by default and what `03-shell-and-layout.md` rules out. The
     ribbon halves rather than stacks: four cells and three is still one glance,
     where seven full-width rows on a phone is a list. */
  @media (max-width: 860px) {
    .matrix {
      grid-template-columns: 1fr;
    }
    .col {
      flex-direction: row;
      flex-wrap: wrap;
      align-items: baseline;
      column-gap: 16px;
    }
    .col__h {
      flex-basis: 100%;
    }
    .cell {
      flex-direction: row;
      align-items: baseline;
      gap: 7px;
    }
  }
  @media (max-width: 620px) {
    .ribbon {
      grid-template-columns: repeat(4, 1fr);
    }
  }
</style>
